import React, { useState, useEffect, useRef } from "react";
import { showMainToast } from "utils/showToast";
import { useSession } from "hooks/useSession";

export default function RoleManagerFormModal({ show, onClose, mode, userData, onSuccess }) {
  const [errors, setErrors] = useState({});
  const { userid } = useSession();
  const modalRef = useRef(null);

  useEffect(() => {
    const modalEl = modalRef.current;
    if (!modalEl) return;
    const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
    if (show) {
      modal.show();
      setErrors({});
    } else {
      modal.hide();
    }
  }, [show]);

  const validateForm = () => {
    const rolenameInput = document.getElementById("rolename");
    const rolename = rolenameInput ? rolenameInput.value.trim() : "";
    let newErrors = {};

    if (!rolename) {
      newErrors.rolename = "Role name is required.";
    } else if (rolename.length < 3) {
      newErrors.rolename = "Role name must be at least 3 characters.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showMainToast("Please correct the errors before saving.", "danger");
      return;
    }

    const rolenameInput = document.getElementById("rolename");
    const rolename = rolenameInput ? rolenameInput.value.trim() : "";

    const formData = new FormData();
    formData.append("rolename", rolename);
    formData.append("action", "ADDUpdate");
    formData.append("userid", userid);
    formData.append("id", userData?.id || "");

    try {
      const response = await fetch("https://pro.mokshiq.in/role_manager_action", {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: formData,
      });

      if (!response.ok) {
        showMainToast("Something went wrong!", "danger");
        return;
      }

      const result = await response.json();
      if (result.status) {
        showMainToast(result.data || "Success!", "success");
        onClose();
        if (onSuccess) onSuccess();
      } else {
        showMainToast(result.data || "Something went wrong!", "danger");
      }
    } catch (error) {
      showMainToast("Something went wrong!", "danger");
    }
  };

  return (
    <div className="modal fade" ref={modalRef} tabIndex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "520px" }}>
        <div
          className="modal-content border-0 shadow-lg position-relative"
          style={{ borderRadius: "24px", overflow: "hidden", backgroundColor: "#ffffff" }}
        >
          {/* Header Card */}
          <div
            className="p-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #f5efff 0%, #ede5ff 100%)",
              borderBottom: "1px solid #e9dcfd"
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "54px",
                    height: "54px",
                    borderRadius: "50%",
                    backgroundColor: "#e4d3ff",
                    color: "#7e57c2"
                  }}
                >
                  <i className="ph-duotone ph-users-three" style={{ fontSize: "26px" }}></i>
                </div>
                <div>
                  <h4 className="fw-bold mb-1" style={{ color: "#1e1b4b", fontSize: "20px" }}>
                    {mode === "edit" ? "Edit Role" : "Add Role"}
                  </h4>
                  <p className="mb-0 text-muted" style={{ fontSize: "13px", lineHeight: "1.4", maxWidth: "260px" }}>
                    Create a new role and assign permissions to manage the system.
                  </p>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2">
                {/* Shield Graphic Badge */}
                <div
                  className="d-none d-sm-flex align-items-center justify-content-center me-2 position-relative"
                  style={{ width: "60px", height: "60px" }}
                >
                  <i className="ph-duotone ph-shield-check" style={{ fontSize: "52px", color: "#7e57c2" }}></i>
                </div>

                {/* Close X Button */}
                <button
                  type="button"
                  className="btn p-0 d-flex align-items-center justify-content-center shadow-sm"
                  onClick={onClose}
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "12px",
                    backgroundColor: "#ffffff",
                    border: "none",
                    color: "#475569",
                    cursor: "pointer"
                  }}
                >
                  <i className="ph-bold ph-x" style={{ fontSize: "16px" }}></i>
                </button>
              </div>
            </div>
          </div>

          {/* Form Body */}
          <div className="p-4">
            <form id="form-validation" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
              <div className="mb-4">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <label className="fw-bold mb-0" htmlFor="rolename" style={{ color: "#0f172a", fontSize: "14.5px" }}>
                    Role Name <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <span
                    className="badge rounded-pill d-inline-flex align-items-center gap-1 px-2.5 py-1"
                    style={{ backgroundColor: "#ede9fe", color: "#6d28d9", fontWeight: "600", fontSize: "12px" }}
                  >
                    <i className="ph-fill ph-info" style={{ fontSize: "13px" }}></i>
                    Enter a unique role name
                  </span>
                </div>

                <div className="position-relative">
                  <div
                    className="position-absolute d-flex align-items-center justify-content-center"
                    style={{
                      left: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: "36px",
                      height: "36px",
                      borderRadius: "10px",
                      backgroundColor: "#f3e8ff",
                      color: "#7e57c2"
                    }}
                  >
                    <i className="ph-duotone ph-user" style={{ fontSize: "18px" }}></i>
                  </div>
                  <input
                    className={`form-control ${errors.rolename ? "is-invalid" : ""}`}
                    id="rolename"
                    name="rolename"
                    type="text"
                    placeholder="Enter role name (e.g. Admin, Manager)"
                    key={userData?.id || mode}
                    defaultValue={userData?.Name || userData?.name || userData?.rolename || ""}
                    style={{
                      height: "52px",
                      paddingLeft: "54px",
                      borderRadius: "14px",
                      borderColor: errors.rolename ? "#ef4444" : "#d8b4fe",
                      fontSize: "14.5px",
                      color: "#0f172a",
                      backgroundColor: "#ffffff"
                    }}
                  />
                </div>
                {errors.rolename && (
                  <div className="text-danger mt-2 fw-medium" style={{ fontSize: "13px" }}>
                    {errors.rolename}
                  </div>
                )}
              </div>

              {/* Divider & Footer Buttons */}
              <div className="pt-3 border-top d-flex align-items-center gap-3">
                <button
                  type="button"
                  className="btn d-inline-flex align-items-center justify-content-center gap-2 flex-grow-1"
                  onClick={onClose}
                  style={{
                    height: "48px",
                    borderRadius: "14px",
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    color: "#334155",
                    fontWeight: "600",
                    fontSize: "15px"
                  }}
                >
                  <i className="ph-bold ph-x" style={{ fontSize: "16px" }}></i>
                  <span>Cancel</span>
                </button>

                <button
                  type="button"
                  className="btn d-inline-flex align-items-center justify-content-center gap-2 flex-grow-1 text-white shadow-sm"
                  onClick={handleSave}
                  style={{
                    height: "48px",
                    borderRadius: "14px",
                    backgroundColor: "#7e57c2",
                    background: "linear-gradient(135deg, #7e57c2 0%, #6366f1 100%)",
                    border: "none",
                    fontWeight: "600",
                    fontSize: "15px"
                  }}
                >
                  <i className="ph-bold ph-floppy-disk" style={{ fontSize: "18px" }}></i>
                  <span>{mode === "edit" ? "Update" : "Save"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

